import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';

import { getServiceCategoriesQuery } from '@/api/booking/getServiceCategoriesQuery';
import { getServicesQuery } from '@/api/booking/getServicesQuery';
import { shopDetailQuery } from '@/api/shop/shopDetailQuery';
import { useIsCategoryFirstEnabled } from '@/hooks/flags/useIsCategoryFirstEnabled';
import { useSuspenseProfessionalQuery } from '@/hooks/queries/booking/useSuspenseProfessionalQuery';
import { assignUncategorizedToOther } from '@/utils/booking/assignUncategorizedToOther';
import { getServiceListMode } from '@/utils/booking/getServiceListMode';

import type {
  UseServiceListModeParams,
  UseServiceListModeResult,
} from './types';

/**
 * Whether the services screen shows the flat grouped list, goes through
 * the category lists, or shows the plain list because no category group is
 * usable. Suspends while it loads, so it must be called under
 * `Suspense`. It never reads the cart: the service count is taken before the
 * cart narrows anything.
 */
export function useServiceListMode({
  shopRoute,
  barberIdOrRoute,
  isCategoryFirstEnabled: isCategoryFirstEnabledOverride,
}: UseServiceListModeParams): UseServiceListModeResult {
  const { data: shop } = useSuspenseQuery(shopDetailQuery(shopRoute));
  const professional = useSuspenseProfessionalQuery({
    shopId: shop.id,
    barberIdOrRoute,
  });
  const [{ data: categories }, { data: servicesData }] = useSuspenseQueries({
    queries: [
      getServiceCategoriesQuery({
        brandId: shop.brandId,
        shopId: shop.id,
        barberId: professional?.barber.id,
      }),
      getServicesQuery(shop.id, professional?.barber.id),
    ],
  });

  const liveIsCategoryFirstEnabled = useIsCategoryFirstEnabled(shop);
  const isCategoryFirstEnabled =
    isCategoryFirstEnabledOverride ?? liveIsCategoryFirstEnabled;
  const mode = isCategoryFirstEnabled
    ? getServiceListMode({
        services: assignUncategorizedToOther(
          servicesData.services,
          categories.rows,
        ),
        rows: categories.rows,
        requireBarberAssignment: barberIdOrRoute === undefined,
      })
    : 'categories';

  return { mode, isCategoryFirstEnabled };
}
